import { Static, Type } from '@sinclair/typebox';
import { FileUploadSchema } from '../../shared/file/file-request.schema';
import { PaginationQueryParamsSchema } from '../../shared/utils/pagination-query-params.schema';

export const GetUserProfileParamsSchema = Type.Object({
  user_id: Type.String({ minLength: 1, description: 'The ID of the User to retrieve.' }),
});

export const GetUserPictureParamsSchema = Type.Object({
  user_id: Type.String({
    minLength: 1,
    description:
      'The ID of the User whose profile picture must be downloaded. Use `me` to specify the owner of the connected account.',
  }),
});

export const GetUserPresenceParamsSchema = Type.Object({
  user_id: Type.String({
    minLength: 1,
    description:
      'The ID of the User whose presence must be retrieved, as returned by the provider. Use `me` to specify the owner of the connected account, although providers do not always report the presence of the account owner.',
  }),
});

export const GetUserProfileBaseQuerySchema = Type.Object({
  with_sections: Type.Optional(Type.Array(Type.String())),
  variant: Type.Optional(Type.String()),
});

export const UpdateUserProfileParamsSchema = Type.Object({
  user_id: Type.String({
    minLength: 1,
    description: 'The ID of the User to update. Use `me` to update the account owner profile.',
  }),
});

export const UpdateUserProfileBaseBodySchema = Type.Object({
  first_name: Type.Optional(
    Type.String({ description: 'The first name of the User. Updating this field is not supported by LinkedIn and Instagram.' }),
  ),
  last_name: Type.Optional(
    Type.String({ description: 'The last name of the User. Updating this field is not supported by LinkedIn and Instagram.' }),
  ),
  description: Type.Optional(
    Type.String({ description: 'Description of the User. Updating this field is not supported by LinkedIn and Instagram.' }),
  ),
  location: Type.Optional(
    Type.String({
      description: 'Location of the User. Omit it to leave it unchanged. Updating this field is not supported by Instagram.',
    }),
  ),
  bio: Type.Optional(
    Type.String({ description: 'Bio / About section. Omit it to leave it unchanged or use an empty string to clear it.' }),
  ),
  picture: Type.Optional(FileUploadSchema),
  background_picture: Type.Optional(FileUploadSchema),
  specifics: Type.Optional(Type.Any()),
});

export const VisitProfileQuerySchema = Type.Object({
  token: Type.String({
    description:
      'The `notify_visit_token` field that you may get from a profile result, depending on whether the provider supports it.',
  }),
});

export const GetUserRelationsParamsSchema = Type.Object({
  user_id: Type.String({ minLength: 1, description: 'The ID of the User to get the relations from.' }),
});

export const GetUserRelationsQuerySchema = Type.Intersect([
  Type.Object({
    search: Type.Optional(Type.String({ description: 'Any text input to filter out results (if supported by the provider).' })),
  }),
  PaginationQueryParamsSchema,
]);

export const DeleteRelationParamsSchema = Type.Object({
  user_id: Type.String({ minLength: 1, description: 'The ID of the User to delete the relation with.' }),
});

export const ListUserFollowersParamsSchema = Type.Object({
  user_id: Type.String({
    minLength: 1,
    description: 'The ID of the User to get the followers from. Use `me` to get the followers of the account owner.',
  }),
});

export const ListUserFollowersQuerySchema = PaginationQueryParamsSchema;

export const ListUserFollowingParamsSchema = Type.Object({
  user_id: Type.String({
    minLength: 1,
    description: 'The ID of the User to get the followed users from. Use `me` to get the followed users of the account owner.',
  }),
});

export const ListUserFollowingQuerySchema = PaginationQueryParamsSchema;

export const FollowUserParamsSchema = Type.Object({
  user_id: Type.String({ minLength: 1, description: 'The ID of the User to follow.' }),
});

export const UnfollowUserParamsSchema = Type.Object({
  user_id: Type.String({ minLength: 1, description: 'The ID of the User to unfollow.' }),
});

export const BlockUserParamsSchema = Type.Object({
  user_id: Type.String({ minLength: 1, description: 'The ID of the User to block.' }),
});

export const UnblockUserParamsSchema = Type.Object({
  user_id: Type.String({ minLength: 1, description: 'The ID of the User to unblock.' }),
});

export const UnblockUserBaseBodySchema = Type.Object({
  specifics: Type.Optional(Type.Any()),
});

export type GetUserProfileParams = Static<typeof GetUserProfileParamsSchema>;
export type GetUserPictureParams = Static<typeof GetUserPictureParamsSchema>;
export type GetUserPresenceParams = Static<typeof GetUserPresenceParamsSchema>;
export type GetUserProfileBaseQuery = Static<typeof GetUserProfileBaseQuerySchema>;
export type UpdateUserProfileParams = Static<typeof UpdateUserProfileParamsSchema>;
export type UpdateUserProfileBaseBody<Specifics = object> = Omit<Static<typeof UpdateUserProfileBaseBodySchema>, 'specifics'> & {
  specifics?: Specifics;
};
export type VisitProfileQuery = Static<typeof VisitProfileQuerySchema>;
export type GetUserRelationsParams = Static<typeof GetUserRelationsParamsSchema>;
export type GetUserRelationsQuery = Static<typeof GetUserRelationsQuerySchema>;
export type DeleteRelationParams = Static<typeof DeleteRelationParamsSchema>;
export type ListUserFollowersParams = Static<typeof ListUserFollowersParamsSchema>;
export type ListUserFollowersQuery = Static<typeof ListUserFollowersQuerySchema>;
export type ListUserFollowingParams = Static<typeof ListUserFollowingParamsSchema>;
export type ListUserFollowingQuery = Static<typeof ListUserFollowingQuerySchema>;
export type FollowUserParams = Static<typeof FollowUserParamsSchema>;
export type UnfollowUserParams = Static<typeof UnfollowUserParamsSchema>;
export type BlockUserParams = Static<typeof BlockUserParamsSchema>;
export type UnblockUserParams = Static<typeof UnblockUserParamsSchema>;
export type UnblockUserBaseBody<Specifics = object> = Omit<Static<typeof UnblockUserBaseBodySchema>, 'specifics'> & {
  specifics?: Specifics;
};
